import { useEffect, useState } from "react";
import { Link } from "@tanstack/react-router";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useLaunchPromo } from "@/lib/use-launch-promo";
import { useAuth } from "@/lib/use-auth";
import { useSubscription } from "@/lib/use-subscription";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Rocket, Star } from "lucide-react";

const SEEN_KEY = "chefconnect.launch_popup_seen";

export function LaunchPopup() {
  const { data } = useLaunchPromo();
  const { user } = useAuth();
  const sub = useSubscription();
  // Verifica founder direttamente dalla riga chef.
  const chefQ = useQuery({
    enabled: !!user,
    queryKey: ["my-chef-founder", user?.id],
    queryFn: async () => {
      const { data } = await supabase
        .from("chefs").select("founder_badge").eq("user_id", user!.id).maybeSingle();
      return data;
    },
  });
  const [open, setOpen] = useState(false);

  // Popup nascosto a chef già Premium / Founder: l'offerta non si applica a loro.
  const hideForUser =
    !!user && (sub.isPremium || chefQ.data?.founder_badge === true);

  useEffect(() => {
    if (!data || !data.active || data.remaining <= 0) return;
    if (sub.loading) return;
    if (hideForUser) return;
    if (typeof window === "undefined") return;
    if (window.sessionStorage.getItem(SEEN_KEY)) return;
    const t = window.setTimeout(() => setOpen(true), 800);
    return () => window.clearTimeout(t);
  }, [data, sub.loading, hideForUser]);

  const close = () => {
    setOpen(false);
    if (typeof window !== "undefined") window.sessionStorage.setItem(SEEN_KEY, "1");
  };

  if (!data) return null;
  if (hideForUser) return null;

  return (
    <Dialog open={open} onOpenChange={(o) => (o ? setOpen(true) : close())}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">
            <Rocket className="mr-2 inline h-5 w-5 text-gold" />
            Diventa uno dei primi Chef della piattaforma
          </DialogTitle>
          <DialogDescription>
            Per il lancio vogliamo premiare i primi professionisti che entreranno nella community.
          </DialogDescription>
        </DialogHeader>
        <ul className="my-2 space-y-2 text-sm">
          {[
            "30 giorni di Premium gratis",
            "Maggiore visibilità sulla home",
            "Più richieste dai clienti",
            "Badge Premium Silver",
            "Priorità nei risultati di ricerca",
            "Badge Founder permanente",
          ].map((b) => (
            <li key={b} className="flex items-center gap-2">
              <Star className="h-4 w-4 fill-gold text-gold" /> {b}
            </li>
          ))}
        </ul>
        <div className="mt-2 rounded-md border border-gold/40 bg-gold-muted/30 px-3 py-2 text-center text-sm font-medium">
          🎁 {data.remaining} / {data.max_chefs} posti ancora disponibili
        </div>
        <div className="mt-2 flex gap-2">
          <Button variant="outline" className="flex-1" onClick={close}>
            Più tardi
          </Button>
          <Button asChild className="flex-1 bg-gold text-gold-foreground hover:bg-gold/90">
            <Link to="/diventa-chef" onClick={close}>Ottieni il Bonus</Link>
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}